// src/hooks/useDropdownOptions.js
import { useState, useEffect } from 'react';
import dropdownService from '../services/dropDownService';

/**
 * Hook personnalisé pour gérer les options d'une liste déroulante avec cache intelligent
 * @param {string} categoryId - L'identifiant de la catégorie de liste déroulante
 * @param {Object} options - Options de configuration
 * @param {boolean} options.withMetadata - Inclure les métadonnées dans les options
 * @param {number} options.cacheMaxAge - La durée maximale du cache en millisecondes (5 minutes par défaut)
 * @param {boolean} options.skipCache - Ignorer le cache et toujours charger depuis l'API
 * @returns {Object} Un objet contenant les options, l'état de chargement et les erreurs
 */
const useDropdownOptions = (categoryId, { withMetadata = false, cacheMaxAge = 300000, skipCache = false } = {}) => {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastRefreshTime, setLastRefreshTime] = useState(null);

    useEffect(() => {
        // Si aucun categoryId n'est fourni, ne rien faire
        if (!categoryId) {
            setOptions([]);
            setLoading(false);
            return;
        }

        const fetchOptions = async () => {
            try {
                setLoading(true);

                // Vérifier d'abord s'il y a des options en cache, sauf si skipCache est à true
                let cachedOptions = null;
                if (!skipCache && !dropdownService.shouldRefreshCache(categoryId, cacheMaxAge)) {
                    cachedOptions = dropdownService.getCachedOptions(categoryId, cacheMaxAge);
                }

                if (cachedOptions) {
                    // Utiliser les options du cache
                    setOptions(cachedOptions);
                    setError(null);
                } else {
                    // Sinon récupérer depuis l'API
                    let fetchedOptions;

                    if (withMetadata) {
                        fetchedOptions = await dropdownService.getOptionsWithMetadata(categoryId);
                    } else {
                        fetchedOptions = await dropdownService.getOptionsForSelect(categoryId);
                    }

                    // Mettre en cache les options récupérées si on n'ignore pas le cache
                    if (!skipCache) {
                        dropdownService.cacheOptions(categoryId, fetchedOptions);
                    }

                    setOptions(fetchedOptions);
                    setError(null);
                    setLastRefreshTime(Date.now());
                }
            } catch (err) {
                console.error(`Erreur lors du chargement des options pour ${categoryId}:`, err);
                setError(`Impossible de charger les options. ${err.message || ""}`);
                // Fallback vers des options vides en cas d'erreur
                setOptions([]);
            } finally {
                setLoading(false);
            }
        };

        fetchOptions();
    }, [categoryId, withMetadata, skipCache]); // Retiré cacheMaxAge des dépendances pour éviter des rechargements inutiles

    /**
     * Fonction pour actualiser les options
     * @param {boolean} invalidateCache - Invalider le cache avant de rafraîchir
     */
    const refreshOptions = async (invalidateCache = true) => {
        if (!categoryId) return;

        try {
            setLoading(true);

            // Invalider le cache si demandé
            if (invalidateCache) {
                dropdownService.invalidateCache(categoryId);
            }

            // Récupérer les nouvelles options
            let fetchedOptions;

            if (withMetadata) {
                fetchedOptions = await dropdownService.getOptionsWithMetadata(categoryId);
            } else {
                fetchedOptions = await dropdownService.getOptionsForSelect(categoryId);
            }

            // Mettre à jour le cache
            dropdownService.cacheOptions(categoryId, fetchedOptions);
            setLastRefreshTime(Date.now());

            setOptions(fetchedOptions);
            setError(null);
        } catch (err) {
            console.error(`Erreur lors de l'actualisation des options pour ${categoryId}:`, err);
            setError(`Impossible d'actualiser les options. ${err.message || ""}`);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Obtenir une option spécifique par sa valeur
     * @param {string} value - La valeur de l'option à rechercher
     * @returns {Object|null} L'option trouvée ou null
     */
    const getOptionByValue = (value) => {
        return options.find(option => option.value === value) || null;
    };

    return {
        options,
        loading,
        error,
        refreshOptions,
        getOptionByValue,
        lastRefreshTime
    };
};

export default useDropdownOptions;